import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid,
  AppBar,
  Toolbar,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Avatar,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Rating,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Paper,
  Badge,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import StarIcon from '@mui/icons-material/Star';
import VerifiedIcon from '@mui/icons-material/Verified';
import VideocamIcon from '@mui/icons-material/Videocam';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import ChatIcon from '@mui/icons-material/Chat';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import logo from '../assets/logo0.png';
import expertImage1 from '../assets/expert1.jpg';
import expertImage2 from '../assets/expert2.jpg';
import expertImage3 from '../assets/expert3.jpg';
import expertBanner from '../assets/expert-banner.jpg';
import complaintsImg from '../assets/Complaints.png';

// Styled Components
const StyledAppBar = styled(AppBar)({
  backgroundColor: '#000000',
  boxShadow: 'none',
  position: 'relative',
  zIndex: 1000,
});

const SocialIcon = styled(IconButton)({
  color: '#ffffff',
  marginLeft: '8px',
  '&:hover': {
    color: '#4CAF50',
  },
});

const HeroSection = styled(Box)({
  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.75)), url(${expertBanner})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  color: '#ffffff',
  padding: '150px 0 120px',
  position: 'relative',
  boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
  borderBottom: '4px solid #4CAF50',
});

const LogoWrapper = styled(Box)({
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  padding: '6px 12px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
});

const ExpertCard = styled(Card)({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.1)',
  },
});

const BookingForm = styled(Paper)({
  padding: '30px',
  borderRadius: '10px',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
});

const FooterWrapper = styled(Box)({
  backgroundColor: '#000000',
  color: '#ffffff',
  padding: '60px 0 20px',
});

const FooterTitle = styled(Typography)({
  marginBottom: '20px',
  fontWeight: 600,
});

const FooterLink = styled(RouterLink)({
  color: '#B0B0B0',
  textDecoration: 'none',
  display: 'block',
  marginBottom: '10px',
  transition: 'color 0.3s ease',
  '&:hover': {
    color: '#4CAF50',
  },
});

const StyledTextField = styled(TextField)({
  marginBottom: '20px',
});

const StyledButton = styled(Button)({
  backgroundColor: '#4CAF50',
  color: '#ffffff',
  padding: '10px 25px',
  borderRadius: '5px',
  fontWeight: 600,
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#388E3C',
    transform: 'translateY(-3px)',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  },
});

const BenefitItem = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: '25px',
});

const BenefitIcon = styled(Box)({
  marginRight: '15px',
  color: '#4CAF50',
});

// Main Component
const Expertconsultation = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [consultationType, setConsultationType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  
  // Experts data
  const experts = [
    {
      id: 1,
      name: 'Dr. Sarah Johnson',
      image: expertImage1,
      specialty: 'Software Architecture & System Design',
      experience: '12 years',
      rating: 4.9,
      reviews: 127,
      price: 120,
      availability: 'Mon-Fri',
      education: 'Ph.D. Computer Science',
      bio: 'Dr. Sarah is a software architecture expert with over 12 years of experience in designing large-scale enterprise systems. She specializes in microservices architecture, cloud computing, and distributed systems.',
    },
    {
      id: 2,
      name: 'Michael Chen',
      image: expertImage2,
      specialty: 'Cybersecurity & Network Infrastructure',
      experience: '10 years',
      rating: 4.8,
      reviews: 98,
      price: 110,
      availability: 'Mon-Thu',
      education: 'MSc Information Security',
      bio: 'Michael is a certified cybersecurity professional with expertise in network security, penetration testing, and security compliance. He has helped numerous organizations strengthen their security posture and implement robust defense mechanisms.',
    },
    {
      id: 3,
      name: 'Lisa Rodriguez',
      image: expertImage3,
      specialty: 'Data Science & ML Engineering',
      experience: '8 years',
      rating: 4.7,
      reviews: 85,
      price: 100,
      availability: 'Wed-Sat',
      education: 'MSc Data Science',
      bio: 'Lisa specializes in data science, machine learning, and AI implementations. With 8 years of industry experience, she has helped businesses transform their operations through data-driven insights and ML solutions.',
    },
  ];
  
  // Mock available time slots
  const timeSlots = ["9:00 AM", "10:00 AM", "11:00 AM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM"];
  
  // Handle scroll event for navbar appearance
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const handleExpertSelect = (expert) => {
    setSelectedExpert(expert);
    window.scrollTo({
      top: document.getElementById('booking-section').offsetTop - 100,
      behavior: 'smooth',
    });
  };
  
  const handleBookingSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulating API call
    setTimeout(() => {
      setIsSubmitting(false);
      setFormSubmitted(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormSubmitted(false);
        setSelectedExpert(null);
        setSelectedDate('');
        setSelectedTime('');
        setConsultationType('');
      }, 3000);
    }, 1500);
  };
  
  return (
    <Box>
      {/* Navbar */}
      <StyledAppBar position={isScrolled ? 'fixed' : 'static'} elevation={isScrolled ? 4 : 0}>
        <Toolbar>
          <LogoWrapper sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img src={logo} alt="Warrili Logo" style={{ height: '45px', marginRight: '10px' }} />
            <Typography variant="h5" component="div" color="primary" fontWeight="bold">
              Warrili
            </Typography>
          </LogoWrapper>
          
          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            <Button 
              color="inherit" 
              component={RouterLink} 
              to="/"
              sx={{ 
                mx: 0.5, 
                fontWeight: 600,
                '&:hover': { color: '#4CAF50' }
              }}
            >
              Home
            </Button>
            <Button 
              color="inherit" 
              component={RouterLink} 
              to="/services"
              sx={{ 
                mx: 0.5, 
                fontWeight: 600,
                '&:hover': { color: '#4CAF50' }
              }}
            >
              Services
            </Button>
            <Button 
              color="inherit" 
              component={RouterLink} 
              to="/solutions"
              sx={{ 
                mx: 0.5, 
                fontWeight: 600,
                '&:hover': { color: '#4CAF50' }
              }}
            >
              Solutions
            </Button>
            <Button 
              color="inherit" 
              component={RouterLink} 
              to="/support"
              sx={{ 
                mx: 0.5, 
                fontWeight: 600,
                '&:hover': { color: '#4CAF50' }
              }}
            >
              Support
            </Button>
            <Button 
              color="inherit" 
              component={RouterLink} 
              to="/pricing"
              sx={{ 
                mx: 0.5, 
                fontWeight: 600,
                '&:hover': { color: '#4CAF50' }
              }}
            >
              Pricing
            </Button>
            <Button 
              color="inherit" 
              component={RouterLink} 
              to="/about"
              sx={{ 
                mx: 0.5, 
                fontWeight: 600,
                '&:hover': { color: '#4CAF50' }
              }}
            >
              About
            </Button>
            <Button 
              color="primary" 
              variant="contained" 
              sx={{ 
                ml: 2, 
                borderRadius: '20px', 
                px: 3,
                boxShadow: '0 4px 8px rgba(76, 175, 80, 0.3)',
                '&:hover': {
                  boxShadow: '0 6px 12px rgba(76, 175, 80, 0.4)',
                  transform: 'translateY(-2px)'
                }
              }} 
              component={RouterLink} 
              to="/login"
            >
              Login
            </Button>
            <Button 
              color="secondary" 
              variant="contained" 
              sx={{ 
                ml: 1,
                borderRadius: '20px', 
                px: 3,
                boxShadow: '0 4px 8px rgba(244, 67, 54, 0.3)',
                '&:hover': {
                  boxShadow: '0 6px 12px rgba(244, 67, 54, 0.4)',
                  transform: 'translateY(-2px)'
                }
              }} 
              component={RouterLink} 
              to="/signup"
            >
              Sign Up
            </Button>
          </Box>
          
          <IconButton
            size="large"
            edge="end"
            color="inherit"
            aria-label="menu"
            sx={{ display: { xs: 'block', md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </StyledAppBar>
      
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box sx={{ position: 'relative' }}>
                <Box 
                  sx={{ 
                    width: '80px', 
                    height: '5px', 
                    backgroundColor: '#4CAF50', 
                    mb: 3 
                  }} 
                />
                <Typography 
                  variant="h1" 
                  component="h1" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 800, 
                    fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                    textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                    mb: 2
                  }}
                >
                  Expert <span style={{ color: '#4CAF50' }}>Consultation</span>
                </Typography>
                <Typography 
                  variant="h5" 
                  component="div" 
                  sx={{ 
                    mb: 4, 
                    width: { md: '90%' },
                    fontWeight: 400,
                    lineHeight: 1.5,
                    textShadow: '1px 1px 2px rgba(0,0,0,0.2)'
                  }}
                >
                  Connect with top tech professionals for personalized guidance and solutions to your complex IT challenges.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 5 }}>
                  <StyledButton 
                    variant="contained" 
                    size="large"
                    sx={{ 
                      py: 1.5, 
                      px: 4, 
                      fontSize: '1.1rem',
                      borderRadius: '30px'
                    }}
                    onClick={() => {
                      document.getElementById('experts-section').scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    Meet Our Experts
                  </StyledButton>
                  <Button 
                    variant="outlined" 
                    size="large" 
                    sx={{ 
                      color: 'white', 
                      borderColor: 'white',
                      borderWidth: '2px',
                      py: 1.5, 
                      px: 4, 
                      fontSize: '1.1rem',
                      borderRadius: '30px',
                      '&:hover': { 
                        borderColor: '#4CAF50', 
                        color: '#4CAF50',
                        backgroundColor: 'rgba(255,255,255,0.1)' 
                      } 
                    }}
                    component={RouterLink}
                    to="/contact"
                  >
                    Contact Us
                  </Button>
                </Box>
                <Box sx={{ mt: 6, display: 'flex', alignItems: 'center' }}>
                  <Chip 
                    icon={<VerifiedIcon />} 
                    label="Certified Experts" 
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.15)', 
                      color: 'white',
                      mr: 2,
                      '& .MuiChip-icon': { color: '#4CAF50' } 
                    }} 
                  />
                  <Chip 
                    icon={<AccessTimeIcon />} 
                    label="24/7 Support" 
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.15)', 
                      color: 'white',
                      mr: 2,
                      '& .MuiChip-icon': { color: '#4CAF50' } 
                    }} 
                  />
                  <Chip 
                    icon={<StarIcon />} 
                    label="4.9/5 Rating" 
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.15)', 
                      color: 'white',
                      '& .MuiChip-icon': { color: '#4CAF50' } 
                    }} 
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box 
                sx={{ 
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '20px',
                    left: '20px',
                    width: '100%',
                    height: '100%',
                    border: '4px solid #4CAF50',
                    borderRadius: '15px',
                    zIndex: 0
                  }
                }}
              >
                <Paper 
                  elevation={10} 
                  sx={{ 
                    p: 3, 
                    borderRadius: '15px', 
                    background: 'rgba(255,255,255,0.9)',
                    position: 'relative',
                    zIndex: 1 
                  }}
                >
                  <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
                    Why Choose Our Experts?
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircleOutlineIcon sx={{ color: 'green', mr: 1 }} />
                      <Typography variant="body1" color="text.secondary">
                        Industry specialists with proven track records
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircleOutlineIcon sx={{ color: 'green', mr: 1 }} />
                      <Typography variant="body1" color="text.secondary">
                        Tailored solutions for your specific challenges
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircleOutlineIcon sx={{ color: 'green', mr: 1 }} />
                      <Typography variant="body1" color="text.secondary">
                        Flexible consultation formats to fit your needs
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircleOutlineIcon sx={{ color: 'green', mr: 1 }} />
                      <Typography variant="body1" color="text.secondary">
                        Ongoing support and follow-up services
                      </Typography>
                    </Box>
                  </Box>
                  <Button 
                    fullWidth 
                    variant="contained" 
                    color="primary" 
                    sx={{ mt: 2 }}
                    onClick={() => {
                      document.getElementById('booking-section').scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    Book a Session Now
                  </Button>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>
      
      {/* Benefits Section */}
      <Box py={10}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
                Why Choose Our Expert Consultation?
              </Typography>
              <Typography variant="body1" paragraph>
                Our consultation services provide you with direct access to industry-leading professionals ready to help you overcome your technical challenges and optimize your systems.
              </Typography>
              
              <BenefitItem>
                <BenefitIcon>
                  <CheckCircleOutlineIcon fontSize="large" />
                </BenefitIcon>
                <Box>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    Personalized Solutions
                  </Typography>
                  <Typography variant="body2">
                    Get tailored advice and solutions specific to your unique technical requirements and business context.
                  </Typography>
                </Box>
              </BenefitItem>
              
              <BenefitItem>
                <BenefitIcon>
                  <CheckCircleOutlineIcon fontSize="large" />
                </BenefitIcon>
                <Box>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    Time and Cost Efficiency
                  </Typography>
                  <Typography variant="body2">
                    Solve problems faster and avoid costly mistakes with expert guidance from the start.
                  </Typography>
                </Box>
              </BenefitItem>
              
              <BenefitItem>
                <BenefitIcon>
                  <CheckCircleOutlineIcon fontSize="large" />
                </BenefitIcon>
                <Box>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    Knowledge Transfer
                  </Typography>
                  <Typography variant="body2">
                    Learn best practices and gain valuable insights directly from industry experts.
                  </Typography>
                </Box>
              </BenefitItem>
              
              <BenefitItem>
                <BenefitIcon>
                  <CheckCircleOutlineIcon fontSize="large" />
                </BenefitIcon>
                <Box>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    Ongoing Support
                  </Typography>
                  <Typography variant="body2">
                    Build a relationship with our experts for continuous guidance as your needs evolve.
                  </Typography>
                </Box>
              </BenefitItem>
              
              <Box mt={4}>
                <StyledButton variant="contained" size="large">
                  Book a Consultation
                </StyledButton>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative', height: '100%', minHeight: '500px' }}>
                {/* Replace with actual image */}
                <Box
                  sx={{
                    backgroundColor: '#f0f0f0',
                    height: '100%',
                    width: '100%',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="body1" color="textSecondary">
                    Consultation Benefits Image
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* Experts Section */}
      <Box py={10} sx={{ backgroundColor: '#f8f9fa' }} id="experts-section">
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
              Meet Our Experts
            </Typography>
            <Typography variant="h6" color="textSecondary" sx={{ maxWidth: '800px', mx: 'auto' }}>
              Our team of certified professionals brings years of experience across various technical domains
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {experts.map((expert) => (
              <Grid item xs={12} md={4} key={expert.id}>
                <ExpertCard>
                  <CardMedia
                    component="img"
                    height="240"
                    image={expert.image}
                    alt={expert.name}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h5" component="div" fontWeight="bold">
                        {expert.name}
                      </Typography>
                      <VerifiedIcon sx={{ color: '#2196F3', ml: 1 }} />
                    </Box>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      {expert.specialty}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Rating value={expert.rating} precision={0.1} readOnly size="small" />
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {expert.rating} ({expert.reviews} reviews)
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <WorkIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        Experience: {expert.experience}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <SchoolIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {expert.education}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <CalendarTodayIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        Available: {expert.availability}
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {expert.bio}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="h6" color="primary" fontWeight="bold">
                        ${expert.price}/hour
                      </Typography>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: 2 }}>
                    <StyledButton 
                      variant="contained" 
                      fullWidth
                      onClick={() => handleExpertSelect(expert)}
                    >
                      Book Consultation
                    </StyledButton>
                  </CardActions>
                </ExpertCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      
      {/* Booking Section */}
      <Box py={10} id="booking-section">
        <Container maxWidth="md">
          <Box textAlign="center" mb={6}>
            <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
              Book Your Consultation
            </Typography>
            <Typography variant="h6" color="textSecondary" sx={{ maxWidth: '800px', mx: 'auto' }}>
              Schedule a session with our experts and get personalized solutions to your technical challenges
            </Typography>
          </Box>
          
          <BookingForm>
            {formSubmitted ? (
              <Box textAlign="center" py={4}>
                <CheckCircleOutlineIcon sx={{ fontSize: 80, color: '#4CAF50', mb: 2 }} />
                <Typography variant="h4" gutterBottom>
                  Booking Submitted!
                </Typography>
                <Typography variant="body1">
                  We've received your consultation request. One of our team members will contact you shortly to confirm your appointment.
                </Typography>
              </Box>
            ) : (
              <form onSubmit={handleBookingSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel id="expert-select-label">Select Expert</InputLabel>
                      <Select
                        labelId="expert-select-label"
                        value={selectedExpert ? selectedExpert.id : ''}
                        onChange={(e) => {
                          const expert = experts.find(exp => exp.id === e.target.value);
                          setSelectedExpert(expert);
                        }}
                        label="Select Expert"
                        required
                      >
                        {experts.map((expert) => (
                          <MenuItem key={expert.id} value={expert.id}>
                            {expert.name} - {expert.specialty}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Select Date"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      required
                      InputProps={{
                        inputProps: { min: new Date().toISOString().split('T')[0] }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel id="time-select-label">Select Time</InputLabel>
                      <Select
                        labelId="time-select-label"
                        value={selectedTime}
                        onChange={(e) => setSelectedTime(e.target.value)}
                        label="Select Time"
                      >
                        {timeSlots.map((time) => (
                          <MenuItem key={time} value={time}>
                            {time}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel id="type-select-label">Consultation Type</InputLabel>
                      <Select
                        labelId="type-select-label"
                        value={consultationType}
                        onChange={(e) => setConsultationType(e.target.value)}
                        label="Consultation Type"
                      >
                        <MenuItem value="video">Video Consultation</MenuItem>
                        <MenuItem value="phone">Phone Consultation</MenuItem>
                        <MenuItem value="email">Email Consultation</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Your Name"
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Describe your technical issue or consultation needs"
                      multiline
                      rows={4}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box textAlign="center">
                      <StyledButton 
                        type="submit" 
                        variant="contained" 
                        size="large"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          'Submit Booking Request'
                        )}
                      </StyledButton>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            )}
          </BookingForm>
        </Container>
      </Box>
      
      {/* FAQ Section */}
      <Box py={10} sx={{ backgroundColor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
              Frequently Asked Questions
            </Typography>
            <Typography variant="h6" color="textSecondary" sx={{ maxWidth: '800px', mx: 'auto' }}>
              Get answers to common questions about our expert consultation services
            </Typography>
          </Box>
          
          <Grid container justifyContent="center">
            <Grid item xs={12} md={10}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">How long does a typical consultation session last?</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1">
                    Our standard consultation sessions are scheduled for 1 hour. However, we offer flexibility based on the complexity of your issue. You can book multiple sessions or extended sessions if needed.
                  </Typography>
                </AccordionDetails>
              </Accordion>
              
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">What happens if I need to reschedule my consultation?</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1">
                    You can reschedule your consultation up to 24 hours before the scheduled time without any penalty. For rescheduling within 24 hours, please contact our support team directly.
                  </Typography>
                </AccordionDetails>
              </Accordion>
              
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">How do I prepare for my consultation session?</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1">
                    To make the most of your session, we recommend preparing a clear description of your issue, any relevant documentation, and specific questions you'd like to address. For technical issues, screenshots or access to relevant systems (if applicable) can be helpful.
                  </Typography>
                </AccordionDetails>
              </Accordion>
              
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Can I request a specific expert for my consultation?</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1">
                    Yes, you can select a specific expert when booking your consultation. If your preferred expert is not available during your desired time slot, we can recommend other experts with similar expertise.
                  </Typography>
                </AccordionDetails>
              </Accordion>
              
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">What if my issue isn't resolved in a single session?</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1">
                    For complex issues, multiple sessions may be required. After your initial consultation, our expert will provide a recommendation on next steps, which may include follow-up sessions, more in-depth analysis, or other services available through our platform.
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* CTA Section */}
      <Box 
        py={8} 
        bgcolor="#4CAF50" 
        color="white" 
        sx={{
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(90deg, #4CAF50 50%, rgba(76, 175, 80, 0.95) 100%)',
            zIndex: 1
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container alignItems="center" spacing={4}>
            <Grid item xs={12} md={7}>
              <Box>
                <Typography 
                  variant="h3" 
                  component="h2" 
                  gutterBottom 
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: '2rem', md: '2.5rem' },
                    textShadow: '2px 2px 4px rgba(0,0,0,0.2)'
                  }}
                >
              Ready to Solve Your Technical Challenges?
            </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 4, 
                    maxWidth: '600px',
                    opacity: 0.9,
                    lineHeight: 1.6
                  }}
                >
              Book a consultation with our experts today and take the first step toward efficient, effective solutions
            </Typography>
            <Button 
              variant="contained" 
              size="large" 
                  endIcon={<ArrowForwardIcon />}
                  sx={{ 
                    bgcolor: 'white', 
                    color: '#4CAF50',
                    px: 4,
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 0,
                    boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                    transition: 'all 0.3s ease',
                    '&:hover': { 
                      bgcolor: '#000',
                      color: '#fff',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.3)'
                    }
                  }}
                  onClick={() => {
                    document.getElementById('booking-section').scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  BOOK NOW
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(45deg, transparent, rgba(255,255,255,0.1))',
                    zIndex: 1
                  }
                }}
              >
                <Box
                  component="img"
                  src={complaintsImg}
                  alt="Technical Support"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '400px',
                    objectFit: 'contain',
                    transform: 'scale(1.1)',
                    '@keyframes float': {
                      '0%, 100%': {
                        transform: 'translateY(0px) scale(1.1)',
                      },
                      '50%': {
                        transform: 'translateY(-10px) scale(1.1)',
                      },
                    },
                    animation: 'float 6s ease-in-out infinite',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* Footer */}
      <FooterWrapper>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.9)', 
                  p: '4px', 
                  borderRadius: '4px', 
                  mr: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <img src={logo} alt="Warrili Logo" style={{ height: '35px' }} />
                </Box>
                <Typography variant="h6" color="white" fontWeight="bold">
                  Warrili
                </Typography>
              </Box>
              <Typography variant="body2" color="#B0B0B0" sx={{ mb: 2 }}>
                Providing expert technical solutions and consultation services to help businesses overcome their IT challenges.
              </Typography>
              <Box>
                <SocialIcon aria-label="Facebook">
                  <FacebookIcon />
                </SocialIcon>
                <SocialIcon aria-label="Twitter">
                  <TwitterIcon />
                </SocialIcon>
                <SocialIcon aria-label="LinkedIn">
                  <LinkedInIcon />
                </SocialIcon>
                <SocialIcon aria-label="Instagram">
                  <InstagramIcon />
                </SocialIcon>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FooterTitle variant="h6">Services</FooterTitle>
              <FooterLink to="/services">Tech Support</FooterLink>
              <FooterLink to="/expertconsultation">Expert Consultation</FooterLink>
              <FooterLink to="/solutions">IT Solutions</FooterLink>
              <FooterLink to="/remote-support">Remote Support</FooterLink>
              <FooterLink to="/ai-services">AI Services</FooterLink>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FooterTitle variant="h6">Company</FooterTitle>
              <FooterLink to="/about">About Us</FooterLink>
              <FooterLink to="/contact">Contact</FooterLink>
              <FooterLink to="/careers">Careers</FooterLink>
              <FooterLink to="/blog">Blog</FooterLink>
              <FooterLink to="/faq">FAQ</FooterLink>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FooterTitle variant="h6">Legal</FooterTitle>
              <FooterLink to="/terms">Terms of Service</FooterLink>
              <FooterLink to="/privacy">Privacy Policy</FooterLink>
              <FooterLink to="/cookies">Cookie Policy</FooterLink>
              <FooterLink to="/security">Security</FooterLink>
              <FooterLink to="/compliance">Compliance</FooterLink>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="#B0B0B0">
              © {new Date().getFullYear()} Warrili. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </FooterWrapper>
    </Box>
  );
};

export default Expertconsultation;
